import { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

interface NewThreadModalProps {
  initialBoard?: 'feedback' | 'theory' | 'fanart';
  onClose: () => void;
  onSuccess: () => void;
}

export default function NewThreadModal({ initialBoard = 'feedback', onClose, onSuccess }: NewThreadModalProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [board, setBoard] = useState<'feedback' | 'theory' | 'fanart'>(initialBoard);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB.');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile || !user) return null;
    setUploading(true);
    try {
      const ext = imageFile.name.split('.').pop();
      const path = `fanart/${user.id}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from('forum-images')
        .upload(path, imageFile, { upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from('forum-images').getPublicUrl(path);
      return data.publicUrl;
    } catch (err) {
      console.error('Image upload failed:', err);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user || !title.trim() || !content.trim()) return;

    setSubmitting(true);
    try {
      let finalImageUrl: string | null = null;
      if (board === 'fanart' && imageFile) {
        finalImageUrl = await uploadImage();
        if (!finalImageUrl) {
          alert('Image upload failed. Please try again.');
          setSubmitting(false);
          return;
        }
      }

      const { error } = await supabase.from('forum_threads').insert({
        title: title.trim(),
        content: content.trim(),
        board,
        image_url: finalImageUrl,
        author_id: user.id,
      });

      if (error) throw error;

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating thread:', error);
      alert('Failed to create post. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6 bg-black/80 backdrop-blur-sm">
      <div className="bg-zinc-900 border border-red-900/30 w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto rounded-t-xl sm:rounded-none">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 sm:p-6 border-b border-red-900/20">
          <h2 className="text-lg sm:text-2xl font-['Cinzel'] text-zinc-100">Create New Post</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-300 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Form */}
        <div className="px-4 py-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Board Selector */}
          <div>
            <label className="block text-sm font-['Cinzel'] text-zinc-300 mb-2">Board</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'feedback', label: 'Story Feedback', icon: 'ri-chat-heart-line' },
                { value: 'theory', label: 'Theory Crafting', icon: 'ri-question-mark' },
                { value: 'fanart', label: 'Fan Art', icon: 'ri-image-line' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setBoard(option.value as typeof board)}
                  className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-2 sm:px-4 py-3 border transition-all cursor-pointer ${
                    board === option.value
                      ? 'bg-red-900/20 border-red-900 text-red-400'
                      : 'bg-zinc-900/50 border-red-900/20 text-zinc-400 hover:border-red-900/40'
                  }`}
                >
                  <i className={`${option.icon} text-lg`}></i>
                  <span className="text-xs sm:text-sm font-['Cinzel'] text-center leading-tight">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-['Cinzel'] text-zinc-300 mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a compelling title..."
              maxLength={200}
              className="w-full px-4 py-3 bg-zinc-900/50 border border-red-900/20 text-zinc-300 font-['Crimson_Text'] placeholder-zinc-600 focus:outline-none focus:border-red-900/40 transition-colors text-sm sm:text-base"
            />
            <div className="mt-1 text-xs text-zinc-500 text-right">{title.length}/200</div>
          </div>

          {/* Image Upload (Fan Art only) */}
          {board === 'fanart' && (
            <div>
              <label className="block text-sm font-['Cinzel'] text-zinc-300 mb-2">Artwork Image</label>

              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full max-h-48 sm:max-h-64 object-contain bg-zinc-950 border border-red-900/20"
                  />
                  <button
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-black/70 hover:bg-black text-zinc-300 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                  <p className="mt-1 text-xs text-zinc-500">{imageFile?.name}</p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex flex-col items-center justify-center gap-2 py-7 sm:py-10 border border-dashed border-red-900/30 hover:border-red-900/60 bg-zinc-900/50 hover:bg-zinc-900/80 transition-all cursor-pointer"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-red-900/60">
                    <i className="ri-upload-cloud-2-line text-3xl sm:text-4xl"></i>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-['Cinzel'] text-zinc-400">Click to upload your artwork</p>
                    <p className="text-xs text-zinc-600 mt-1">PNG, JPG, GIF, WEBP — max 5MB</p>
                  </div>
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          )}

          {/* Content / Description */}
          <div>
            <label className="block text-sm font-['Cinzel'] text-zinc-300 mb-2">
              {board === 'fanart' ? 'Description' : 'Content'}
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={
                board === 'fanart'
                  ? 'Describe your artwork and inspiration...'
                  : 'Share your thoughts from the shadows...'
              }
              maxLength={2000}
              className="w-full px-4 py-3 bg-zinc-900/50 border border-red-900/20 text-zinc-300 font-['Crimson_Text'] placeholder-zinc-600 focus:outline-none focus:border-red-900/40 transition-colors resize-none text-sm sm:text-base"
              rows={6}
            />
            <div className="mt-1 text-xs text-zinc-500 text-right">{content.length}/2000</div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 sm:gap-3 px-4 py-4 sm:p-6 border-t border-red-900/20">
          <button
            onClick={onClose}
            className="px-4 sm:px-6 py-2 text-zinc-400 hover:text-zinc-300 font-['Cinzel'] text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || !content.trim() || submitting || uploading}
            className="px-4 sm:px-6 py-2 bg-red-900 hover:bg-red-800 text-zinc-100 font-['Cinzel'] text-sm tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {uploading ? 'Uploading...' : submitting ? 'Creating...' : 'Create Post'}
          </button>
        </div>
      </div>
    </div>
  );
}
